import type { WalkerOS } from '@walkeros/core';
import type { Walker } from '@walkeros/web-core';
import type { Scope } from './types';
import { assign, castValue, isArray, trim } from '@walkeros/core';
import { Const } from '@walkeros/collector';
import { getAttribute } from '@walkeros/web-core';

export function getElbAttributeName(
  prefix: string,
  name?: string,
  isProperty = true,
): string {
  // separate dynamic properties from walker Const.Commands
  const separator = isProperty ? '-' : '';
  name = name != undefined ? separator + name : '';
  return prefix + name;
}

export function getElbValues(
  prefix: string,
  element: Element,
  name: string,
  isProperty = true,
): WalkerOS.Properties {
  const attributeValue =
    getAttribute(element, getElbAttributeName(prefix, name, isProperty)) || '';

  const elbValues = splitAttribute(attributeValue).reduce((values, str) => {
    let [key, val]: Walker.KeyVal = splitKeyVal(str);

    if (!key) return values;

    // Handle keys without value
    if (!val) {
      // Manually remove the : from key on empty values
      if (key.endsWith(':')) key = key.slice(0, -1);
      val = '';
    }

    // Dynamic values
    if (val.startsWith('#')) {
      val = val.slice(1); // Remove # symbol
      try {
        // Read property value from element
        let dynamicValue = (element as Element)[val as keyof Element];
        if (!dynamicValue && val === 'selected') {
          // Try to read selected value with chance of error
          dynamicValue = (element as HTMLSelectElement).options[
            (element as HTMLSelectElement).selectedIndex
          ].text;
        }

        val = String(dynamicValue);
      } catch (error) {
        val = '';
      }
    }

    if (key.endsWith('[]')) {
      key = key.slice(0, -2); // Remove [] symbol
      if (!isArray(values[key])) values[key] = [];
      (values[key] as WalkerOS.PropertyType[]).push(castValue(val));
    } else {
      values[key] = castValue(val);
    }

    return values;
  }, {} as WalkerOS.Properties);

  return elbValues;
}

export function getAllEvents(
  scope: Scope,
  prefix: string = Const.Commands.Prefix,
): Walker.Events {
  const actualScope = scope;
  if (!actualScope) return [];
  let events: Walker.Events = [];
  const action = Const.Commands.Action;
  const actionSelector = `[${getElbAttributeName(prefix, action, false)}]`;

  const processElementEvents = (elem: Element) => {
    Object.keys(getElbValues(prefix, elem, action, false)).forEach(
      (trigger) => {
        events = events.concat(getEvents(elem, trigger, prefix));
      },
    );
  };

  // Check if the scope element itself has action attributes
  const doc =
    (actualScope as Element).ownerDocument || (actualScope as Document);
  if (
    actualScope !== doc &&
    (actualScope as Element).matches?.(actionSelector)
  ) {
    processElementEvents(actualScope as Element);
  }

  queryAllComposed(actualScope, actionSelector, processElementEvents);

  return events;
}

export function getEvents(
  target: Element,
  trigger: string,
  prefix: string = Const.Commands.Prefix,
): Walker.Events {
  const events: Walker.Events = [];

  // Check for actions and get entity collection strategy
  const { actions, nearestOnly } = resolveAttributes(prefix, target, trigger);

  // Stop if there's no valid action combo
  if (!actions.length) return events;

  actions.forEach((triggerAction) => {
    const filter = splitAttribute(triggerAction.actionParams || '', ',').reduce(
      (filter, param) => {
        filter[trim(param)] = true;
        return filter;
      },
      {} as Walker.Filter,
    );

    // Get entities - using nearestOnly flag to determine collection strategy
    const entities = getEntities(prefix, target, filter, nearestOnly);

    // Use page as default entity if no one was set
    if (!entities.length) {
      const entity = 'page';
      // Only use explicit page properties and ignore generic properties
      const entitySelector = `[${getElbAttributeName(prefix, entity)}]`;

      // Get matching properties from the element and its parents
      const [data, context] = getThisAndParentProperties(
        target,
        entitySelector,
        prefix,
        entity,
      );

      entities.push({
        entity, // page
        data, // Consider only upper data
        nested: [], // Skip nested in this faked page case
        context,
      });
    }

    // Return a list of full events
    entities.forEach((entity) => {
      events.push({
        entity: entity.entity,
        action: triggerAction.action,
        data: entity.data,
        trigger,
        context: entity.context,
        nested: entity.nested ?? [],
      });
    });
  });

  return events;
}

export function getGlobals(
  prefix: string = Const.Commands.Prefix,
  scope?: Scope,
): WalkerOS.Properties {
  if (!scope) return {};
  const globalsName = getElbAttributeName(
    prefix,
    Const.Commands.Globals,
    false,
  );
  const globalSelector = `[${globalsName}]`;
  let values = {};

  queryAll(scope, globalSelector, (element) => {
    values = assign(
      values,
      getElbValues(prefix, element, Const.Commands.Globals, false),
    );
  });

  return values;
}

export function getPageViewData(
  prefix: string,
  scope: Scope,
): [WalkerOS.Properties, WalkerOS.OrderedProperties] {
  // static page view
  const doc = (scope as Element).ownerDocument || (scope as Document);
  const win = doc.defaultView!;
  const loc = win.location;
  const page = 'page';
  const scopeElement =
    'body' in scope ? (scope as Document).body : (scope as Element);
  const [data, context] = getThisAndParentProperties(
    scopeElement,
    `[${getElbAttributeName(prefix, page)}]`,
    prefix,
    page,
  );
  data.domain = loc.hostname;
  data.title = doc.title;
  data.referrer = doc.referrer;

  if (loc.search) data.search = loc.search;
  if (loc.hash) data.hash = loc.hash;

  // @TODO get all nested entities
  return [data, context];
}

export function getTriggerActions(str: string): Walker.TriggersActionGroups {
  const values: Walker.TriggersActionGroups = {};

  const attributes = splitAttribute(str);

  attributes.forEach((str) => {
    const [triggerAttr, actionAttr] = splitKeyVal(str);
    const [trigger, triggerParams] = parseAttribute(triggerAttr);

    if (!trigger) return;

    let [action, actionParams] = parseAttribute(actionAttr || '');

    // Shortcut if trigger and action are the same (click:click)
    action = action || trigger;

    if (!values[trigger]) values[trigger] = [];

    values[trigger].push({ trigger, triggerParams, action, actionParams });
  });

  return values;
}

export function getEntities(
  prefix: string,
  target: Element,
  filter?: Walker.Filter,
  nearestOnly = false,
): WalkerOS.Entities {
  const entities: WalkerOS.Entities = [];
  let element = target as Node['parentElement'];

  // Unset empty filter object
  filter = Object.keys(filter || {}).length !== 0 ? filter : undefined;

  while (element) {
    const entity = getEntity(prefix, element, target, filter);
    if (entity) {
      entities.push(entity);
      if (nearestOnly) break; // Stop after first entity for data-elbaction
    }

    element = getParent(prefix, element);
  }

  return entities;
}

function getEntity(
  prefix: string,
  element: Element,
  origin?: Element,
  filter?: Walker.Filter,
): WalkerOS.Entity | null {
  const entity = getAttribute(element, getElbAttributeName(prefix));

  // It's not a (valid) entity element or should be filtered
  if (!entity || (filter && !filter[entity])) return null;

  const scopeElems = [element]; // All related elements
  const dataSelector = `[${getElbAttributeName(
    prefix,
    entity,
  )}],[${getElbAttributeName(prefix, '')}]`; // [data-elb-entity,data-elb-]
  const linkName = getElbAttributeName(prefix, Const.Commands.Link, false); // data-elblink

  let data: WalkerOS.Properties = {};
  const nested: WalkerOS.Entities = [];
  const [parentData, context] = getThisAndParentProperties(
    origin || element,
    dataSelector,
    prefix,
    entity,
    true, // collect scoped generic (data-elb_) for real entities
  );

  // Add linked elements (data-elblink)
  queryAllComposed(element, `[${linkName}]`, (link) => {
    const [linkId, linkState]: Walker.KeyVal = splitKeyVal(
      getAttribute(link, linkName),
    );

    // Get all linked child elements if link is a parent
    // Note: Searches entire document including shadow roots.
    // Acceptable because link-parent usage is rare in practice.
    if (linkState === 'parent')
      queryAllComposed(
        element.ownerDocument.body,
        `[${linkName}="${linkId}:child"]`,
        (wormhole) => {
          scopeElems.push(wormhole);

          // A linked child can also be an entity
          const nestedEntity = getEntity(prefix, wormhole);
          if (nestedEntity) nested.push(nestedEntity);
        },
      );
  });

  // Get all property elements including linked elements
  const propertyElems: Array<Element> = [];
  scopeElems.forEach((elem) => {
    // Also check for property on same level
    if (elem.matches(dataSelector)) propertyElems.push(elem);

    queryAllComposed(elem, dataSelector, (elem) => propertyElems.push(elem));
  });

  // Get properties
  let genericData: WalkerOS.Properties = {};
  propertyElems.forEach((child) => {
    // Eventually override closer properties
    genericData = assign(genericData, getElbValues(prefix, child, ''));
    data = assign(data, getElbValues(prefix, child, entity));
  });

  // Merge properties with the hierarchy generic > data > parent
  data = assign(assign(genericData, data), parentData);

  // Get nested entities
  scopeElems.forEach((elem) => {
    queryAllComposed(
      elem,
      `[${getElbAttributeName(prefix)}]`,
      (nestedEntityElement) => {
        const nestedEntity = getEntity(prefix, nestedEntityElement);
        if (nestedEntity) nested.push(nestedEntity);
      },
    );
  });

  return { entity, data, context, nested };
}

function getParent(prefix: string, elem: HTMLElement): HTMLElement | null {
  const linkName = getElbAttributeName(prefix, Const.Commands.Link, false); // data-elblink

  // Link
  if (elem.matches(`[${linkName}]`)) {
    const [linkId, linkState]: Walker.KeyVal = splitKeyVal(
      getAttribute(elem, linkName),
    );
    if (linkState === 'child') {
      // Link-parent lookup does not cross shadow boundaries.
      // Uses simple queryAll (no shadow recursion) since this runs
      // during per-event entity traversal.
      const doc = elem.ownerDocument;
      let found: HTMLElement | null = null;
      queryAll(doc, `[${linkName}="${linkId}:parent"]`, (el) => {
        if (!found) found = el as HTMLElement;
      });
      return found;
    }
  }

  // Shadow DOM traversal
  const win = elem.ownerDocument.defaultView!;
  if (
    !elem.parentElement &&
    elem.getRootNode &&
    elem.getRootNode() instanceof win.ShadowRoot
  ) {
    return (elem.getRootNode() as ShadowRoot).host as HTMLElement;
  }

  return elem.parentElement;
}

function getThisAndParentProperties(
  element: Element,
  entitySelector: string,
  prefix: string,
  type: string,
  collectScoped = false,
): [data: WalkerOS.Properties, context: WalkerOS.OrderedProperties] {
  let data: WalkerOS.Properties = {};
  const context: WalkerOS.OrderedProperties = {};
  let parent = element as Node['parentElement'];
  const contextSelector = `[${getElbAttributeName(
    prefix,
    Const.Commands.Context,
    false,
  )}]`;
  const scopedName = Const.Commands.Scoped; // '_' -> data-elb_
  const scopedSelector = `[${getElbAttributeName(prefix, scopedName, false)}]`;

  // Get all bubbling-up properties with decreasing priority
  let contextI = 0; // Context counter
  while (parent) {
    // Scoped generic (data-elb_): same generic tier, bubble-up only.
    // The synthetic page entity ignores generics, so it skips scoped too.
    // Guard on scopedName so a falsy command constant degrades to "no scoped
    // collection" instead of an empty suffix that would match the bare prefix.
    if (collectScoped && scopedName && parent.matches(scopedSelector)) {
      data = assign(getElbValues(prefix, parent, scopedName, false), data);
    }

    // Properties
    if (parent.matches(entitySelector)) {
      // Get higher properties first
      data = assign(getElbValues(prefix, parent, ''), data); // Generic
      data = assign(getElbValues(prefix, parent, type), data); // Explicit
    }

    // Context
    if (parent.matches(contextSelector)) {
      Object.entries(
        getElbValues(prefix, parent, Const.Commands.Context, false),
      ).forEach(([key, val]) => {
        // Don't override context with same but higher key
        if (val && !context[key]) context[key] = [val, contextI];
      });

      // Increase context counter with each parent level
      ++contextI;
    }

    parent = getParent(prefix, parent);
  }

  return [data, context];
}

function queryAll(
  scope: Document | Element,
  selector: string,
  fn: (element: Element) => void,
): void {
  scope.querySelectorAll(selector).forEach(fn);
}

// Shadow-aware version that recurses into open shadow roots.
// Use for discovery (init) and entity-scoped queries (small subtrees).
// Avoid for per-event document-scoped queries (e.g. getGlobals).
export function queryAllComposed(
  scope: Document | Element | ShadowRoot,
  selector: string,
  fn: (element: Element) => void,
): void {
  scope.querySelectorAll(selector).forEach(fn);

  const doc = (scope as Element).ownerDocument || (scope as Document);
  const win = doc.defaultView!;
  if (scope instanceof win.Element && scope.shadowRoot) {
    queryAllComposed(scope.shadowRoot, selector, fn);
  }
  scope.querySelectorAll('*').forEach((el) => {
    if (el.shadowRoot) {
      queryAllComposed(el.shadowRoot, selector, fn);
    }
  });
}

function resolveAttributes(
  prefix: string,
  target: Element,
  trigger: string,
): { actions: Walker.TriggerActions; nearestOnly: boolean } {
  let element = target as Node['parentElement'];

  while (element) {
    // Check for data-elbactions first (takes precedence)
    const multiAttribute = getAttribute(
      element,
      getElbAttributeName(prefix, Const.Commands.Actions, false),
    );

    if (multiAttribute) {
      const triggerActions = getTriggerActions(multiAttribute);
      if (triggerActions[trigger]) {
        return { actions: triggerActions[trigger], nearestOnly: false };
      }
    }

    // Check for data-elbaction (nearest entity only)
    const singleAttribute = getAttribute(
      element,
      getElbAttributeName(prefix, Const.Commands.Action, false),
    );

    if (singleAttribute) {
      const triggerActions = getTriggerActions(singleAttribute);
      // Action found on element or is not a click trigger
      if (triggerActions[trigger] || trigger !== 'click') {
        return { actions: triggerActions[trigger] || [], nearestOnly: true };
      }
    }

    element = getParent(prefix, element);
  }

  return { actions: [], nearestOnly: false };
}

function splitAttribute(str: string, separator = ';'): Walker.Attributes {
  const values: Walker.Attributes = [];

  if (!str) return values;

  const reg = new RegExp(`(?:[^${separator}']+|'[^']*')+`, 'ig');
  return str.match(reg) || [];
}

function splitKeyVal(str: string): Walker.KeyVal {
  const [key, value] = str.split(/:(.+)/, 2);
  return [trim(key), trim(value)];
}

function parseAttribute(str: string): Walker.KeyVal {
  // action(a, b, c)
  const [key, value] = str.split('(', 2);
  const param = value ? value.slice(0, -1) : ''; // Remove the )
  // key = 'action'
  // param = 'a, b, c'
  return [key, param];
}
